import React, { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  Star, ShieldCheck, MapPin, Calendar, Sparkles, ExternalLink,
  Instagram, Loader2, ArrowRight, MessageSquare, Mail, Images, History, Users,
} from 'lucide-react';
import SEO from '../components/SEO';
import FollowOrganizerButton from '../components/FollowOrganizerButton';
import { orgProfileLD, breadcrumbLD } from '../utils/structuredData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// "eventism.com" stored without protocol renders as a broken relative link —
// normalize before use.
const normalizeUrl = (u) => (/^https?:\/\//i.test(u || '') ? u : `https://${u}`);

function MessageOrganizerDialog({ open, onOpenChange, slug, orgName }) {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSend = async () => {
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 5) {
      toast.error('Please fill in your name, email and a message');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/organizations/public/${slug}/message`, form);
      setSent(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send your message — try again');
    }
    setSending(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="message-organizer-dialog">
        <DialogHeader>
          <DialogTitle>Message {orgName}</DialogTitle>
          <DialogDescription>Ask about events, private bookings, sponsorships — anything.</DialogDescription>
        </DialogHeader>
        {sent ? (
          <div className="py-6 text-center" data-testid="message-sent-confirmation">
            <Mail className="h-10 w-10 mx-auto text-emerald-400 mb-3" />
            <p className="font-semibold">Message sent!</p>
            <p className="text-sm text-muted-foreground mt-1">{orgName} will reply to your email.</p>
            <Button className="mt-4" variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Your name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Smith" data-testid="msg-org-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Your email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" data-testid="msg-org-email" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Message</Label>
              <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Hi! I'd love to know more about…" className="min-h-[110px]" data-testid="msg-org-message" />
            </div>
            <Button className="w-full bg-gradient-to-r from-fuchsia-500 to-indigo-500 text-white hover:opacity-90" onClick={handleSend} disabled={sending} data-testid="msg-org-send">
              {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
              Send message
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * /organizations/{slug} — Public organizer profile page.
 * Phase 4 marketplace surface + Phase 5 SEO (schema.org Organization +
 * AggregateRating + BreadcrumbList JSON-LD).
 */
export default function OrganizationProfile() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [org, setOrg] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [msgOpen, setMsgOpen] = useState(searchParams.get('message') === '1');
  const [showPast, setShowPast] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [profile, revs] = await Promise.all([
          axios.get(`${API}/organizations/public/${slug}`),
          axios.get(`${API}/organizations/public/${slug}/reviews`).catch(() => ({ data: { reviews: [] } })),
        ]);
        if (cancelled) return;
        setOrg(profile.data);
        setReviews(revs.data?.reviews || []);
      } catch (err) {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background pt-20">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
      </div>
    );
  }

  if (notFound || !org) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background pt-20">
        <div className="text-center max-w-md px-6">
          <p className="text-sm uppercase tracking-wider text-muted-foreground">Not found</p>
          <h1 className="text-3xl font-bold mt-2">Organizer not found</h1>
          <p className="text-muted-foreground mt-2">
            This organizer profile doesn't exist or has been deactivated.
          </p>
          <Link to="/organizations">
            <Button className="mt-4" data-testid="org-profile-back-btn">Browse organizers</Button>
          </Link>
        </div>
      </div>
    );
  }

  const displayName = org.display_name || org.legal_name;
  const cityName = org.primary_city
    ? String(org.primary_city).replace(/[-_]/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase())
    : null;

  const breadcrumb = breadcrumbLD([
    { name: 'Home', url: '/' },
    { name: 'Organizers', url: '/organizations' },
    { name: displayName, url: `/organizations/${slug}` },
  ]);
  const profileLD = orgProfileLD(org);

  return (
    <div className="min-h-screen bg-background pt-20 pb-16" data-testid="organization-profile-page">
      <SEO
        title={`${displayName} — Event Organizer on Eventism`}
        description={
          org.bio
            ? org.bio.slice(0, 160)
            : `${displayName} hosts ticketed events on Eventism${cityName ? ` in ${cityName}` : ''}. Browse upcoming events and book tickets.`
        }
        canonical={`/organizations/${slug}`}
        keywords={`${displayName}, event organizer${cityName ? `, ${cityName} events` : ''}, ticketed events, Eventism`}
        ogImage={org.cover_url || org.logo_url || undefined}
        structuredData={[profileLD, breadcrumb].filter(Boolean)}
      />

      {/* Cover hero — featured image of the next upcoming event, falling back
          to the org's own cover */}
      {(() => {
        const nextEventImage = (org.upcoming_events || []).find((ev) => ev.image_url)?.image_url;
        const heroImg = nextEventImage || org.cover_url;
        return (
          <div className="relative h-48 sm:h-64 lg:h-72 overflow-hidden bg-gradient-to-br from-indigo-500/15 via-fuchsia-500/10 to-background">
            {heroImg && (
              <img src={heroImg} alt="" className={`w-full h-full object-cover ${nextEventImage ? 'opacity-70' : 'opacity-50'}`} data-testid="org-profile-hero-img" />
            )}
            <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-background to-transparent" />
          </div>
        );
      })()}

      <div className="max-w-5xl mx-auto px-4 -mt-16 relative space-y-8">

        {/* Identity card */}
        <div className="flex flex-wrap items-end gap-4">
          <div
            className="h-28 w-28 rounded-2xl bg-card border-4 border-background shadow-xl overflow-hidden flex items-center justify-center shrink-0"
            data-testid="org-profile-avatar"
          >
            {org.logo_url ? (
              <img src={org.logo_url} alt={displayName} className="h-full w-full object-contain p-1.5" />
            ) : (
              <Sparkles className="h-10 w-10 text-indigo-300" />
            )}
          </div>
          <div className="flex-1 min-w-[200px] pb-2">
            <h1
              className="text-3xl sm:text-4xl font-bold font-['Space_Grotesk'] flex items-center gap-2"
              data-testid="org-profile-name"
            >
              {displayName}
              {org.is_verified && (
                <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">
                  <ShieldCheck className="h-3 w-3 mr-1" /> Verified
                </Badge>
              )}
            </h1>
            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mt-1">
              {cityName && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" /> {cityName}
                </span>
              )}
              {org.avg_rating != null && (
                <span className="flex items-center gap-1 text-amber-300 font-medium" data-testid="org-profile-rating">
                  <Star className="h-3.5 w-3.5 fill-amber-300" />
                  {Number(org.avg_rating).toFixed(1)} <span className="text-muted-foreground">({org.review_count || 0})</span>
                </span>
              )}
              <span className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" /> {org.events_hosted_count || 0} {(org.events_hosted_count || 0) === 1 ? 'event' : 'events'} hosted
              </span>
            </div>
          </div>
          <div className="flex gap-2 pb-2 flex-wrap">
            <FollowOrganizerButton slug={slug} showCount />
            <Button
              size="sm"
              className="bg-gradient-to-r from-fuchsia-500 to-indigo-500 text-white hover:opacity-90"
              onClick={() => setMsgOpen(true)}
              data-testid="org-profile-message-btn"
            >
              <Mail className="h-3.5 w-3.5 mr-1" /> Message
            </Button>
            {org.website && (
              <a
                href={normalizeUrl(org.website)}
                target="_blank" rel="noreferrer"
                data-testid="org-profile-website-link"
              >
                <Button variant="outline" size="sm">
                  <ExternalLink className="h-3.5 w-3.5 mr-1" /> Website
                </Button>
              </a>
            )}
            {org.instagram && (
              <a
                href={`https://instagram.com/${String(org.instagram).replace('@', '')}`}
                target="_blank" rel="noreferrer"
                data-testid="org-profile-instagram-link"
              >
                <Button variant="outline" size="sm">
                  <Instagram className="h-3.5 w-3.5 mr-1" /> Instagram
                </Button>
              </a>
            )}
          </div>
        </div>

        {/* Bio — plain text, pulled up close to the identity row */}
        {org.bio && (
          <div className="-mt-3" data-testid="org-profile-bio">
            <p className="text-base leading-relaxed whitespace-pre-line text-muted-foreground max-w-3xl">{org.bio}</p>
          </div>
        )}

        {/* Upcoming events */}
        <section data-testid="org-profile-upcoming-section">
          <h2 className="text-xl font-bold mb-3 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-indigo-400" /> Upcoming events
          </h2>
          {(org.upcoming_events || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No upcoming events announced yet — check back soon.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {(org.upcoming_events || []).map((ev) => (
                <Link
                  to={`/events/${ev.city || 'denver'}/${ev.slug}`}
                  key={ev.event_id}
                  data-testid={`org-event-card-${ev.event_id}`}
                  className="block"
                >
                  <Card className="border-border hover:border-indigo-500/40 transition-colors h-full overflow-hidden">
                    {ev.image_url && (
                      <div className="aspect-video bg-card overflow-hidden">
                        <img src={ev.image_url} alt={ev.name} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <CardContent className="p-3">
                      <p className="font-semibold text-sm line-clamp-2">{ev.name}</p>
                      <p className="text-[11px] text-muted-foreground mt-1">
                        {ev.event_date ? new Date(ev.event_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}
                        {ev.venue_name ? ` · ${ev.venue_name}` : ''}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Photo gallery — past event highlights */}
        {(org.gallery_images || []).length > 0 && (
          <section data-testid="org-profile-gallery">
            <h2 className="text-xl font-bold mb-3 flex items-center gap-2">
              <Images className="h-5 w-5 text-fuchsia-400" /> Past event highlights
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {org.gallery_images.slice(0, 8).map((url, i) => (
                <a key={i} href={url} target="_blank" rel="noreferrer" className="block group" data-testid={`org-gallery-img-${i}`}>
                  <div className="aspect-square rounded-xl overflow-hidden border border-border bg-card">
                    <img src={url} alt={`${displayName} past event ${i + 1}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                </a>
              ))}
            </div>
          </section>
        )}

        {/* Memberships & series */}
        {(org.series || []).length > 0 && (
          <section data-testid="org-profile-series-section">
            <h2 className="text-xl font-bold mb-3 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-400" /> Memberships &amp; series
            </h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {org.series.map((s) => (
                <Link to={`/series/${s.slug}`} key={s.brand_id} data-testid={`org-series-card-${s.brand_id}`} className="block group">
                  <Card className="border-border hover:border-amber-500/40 transition-colors h-full overflow-hidden">
                    <CardContent className="p-4 flex items-center gap-3">
                      {(s.logo_url || s.hero_image_url) ? (
                        <img src={s.logo_url || s.hero_image_url} alt={s.name} className="h-12 w-12 rounded-xl object-contain bg-card border border-border p-1 flex-shrink-0" />
                      ) : (
                        <div className="h-12 w-12 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                          <Sparkles className="h-5 w-5 text-amber-400" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm truncate">{s.name}</p>
                        <p className="text-xs text-muted-foreground line-clamp-1">{s.tagline || s.description || 'Recurring events & member perks'}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {s.has_plans && <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 text-[10px]">Membership available</Badge>}
                          {s.members_count > 0 && (
                            <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Users className="h-3 w-3" /> {s.members_count} members</span>
                          )}
                        </div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-1 transition-transform flex-shrink-0" />
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Past events — discreet collapsible text list */}
        {(org.past_events || []).length > 0 && (
          <section data-testid="org-profile-past-section">
            <button
              onClick={() => setShowPast(!showPast)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="org-past-toggle"
            >
              <History className="h-4 w-4" />
              {showPast ? 'Hide' : 'Show'} past events ({org.past_events.length})
            </button>
            {showPast && (
              <div className="mt-3 divide-y divide-border rounded-xl border border-border overflow-hidden" data-testid="org-past-list">
                {org.past_events.map((ev) => (
                  <Link
                    to={`/events/${ev.city || 'denver'}/${ev.slug}`}
                    key={ev.event_id}
                    data-testid={`org-past-event-${ev.event_id}`}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-accent/50 transition-colors"
                  >
                    <span className="text-xs text-muted-foreground w-24 shrink-0">
                      {ev.event_date ? new Date(ev.event_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                    </span>
                    <span className="font-medium truncate flex-1">{ev.name}</span>
                    {ev.venue_name && <span className="text-xs text-muted-foreground truncate hidden sm:block">{ev.venue_name}</span>}
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  </Link>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Reviews */}
        <section data-testid="org-profile-reviews-section">
          <h2 className="text-xl font-bold mb-3 flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-indigo-400" /> Reviews
          </h2>
          {reviews.length === 0 ? (
            <p className="text-sm text-muted-foreground">No reviews yet.</p>
          ) : (
            <div className="space-y-3">
              {reviews.map((r) => (
                <Card key={r.review_id} className="border-border" data-testid={`review-${r.review_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-1 text-amber-300 font-medium">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                          key={i}
                          className={`h-3.5 w-3.5 ${i < r.rating ? 'fill-amber-300' : 'opacity-30'}`}
                        />
                      ))}
                      <span className="ml-2 text-xs text-muted-foreground capitalize">
                        from {r.reviewer_kind}
                      </span>
                      <span className="text-[10px] text-muted-foreground ml-auto">
                        {r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}
                      </span>
                    </div>
                    <p className="text-sm mt-2 whitespace-pre-line">{r.body}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* CTA — venues */}
        <Card className="border-indigo-500/30 bg-gradient-to-br from-indigo-500/10 to-fuchsia-500/5">
          <CardContent className="p-5 sm:p-6 flex flex-wrap items-center gap-4 justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-indigo-300">For venues</p>
              <h3 className="text-lg font-bold mt-1">Want to host {displayName}?</h3>
              <p className="text-sm text-muted-foreground max-w-xl mt-1">
                List your space on Eventism and get discovered by organizers looking for the perfect venue.
              </p>
            </div>
            <Link to="/venues/list-your-venue" data-testid="org-profile-venue-cta">
              <Button className="bg-gradient-to-r from-indigo-500 to-fuchsia-500 hover:from-indigo-400 hover:to-fuchsia-400 text-white">
                List your venue <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      <MessageOrganizerDialog open={msgOpen} onOpenChange={setMsgOpen} slug={slug} orgName={displayName} />
    </div>
  );
}
